import { HexagonLayer } from '@deck.gl/aggregation-layers';
import { GoogleMapsOverlay as DeckOverlay } from '@deck.gl/google-maps';
import { Layer } from '@deck.gl/layers';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import tinycolor from 'tinycolor2';

import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { SnWidget } from '@lib/api/entities';
import { AnalyticsService } from '@lib/api/services';
import { SnCurrentOrganization } from '@lib/auth';
import { FsTimeService } from '@lib/common/services';
import { GoogleMap, GoogleMapPopup, GoogleMapStyle } from '@lib/geo/google/map';
import { SnLonLatLike } from '@lib/geo/helpers';

import { AnalyticsWidgetDateComponent } from '../../components/analytics-widget-date/analytics-widget-date.component';
import { PolylineInfoPopoutComponent } from '../../components/polyline-info-popout/polyline-info-popout.component';

@Component({
  selector: 'sn-analytics-widget',
  templateUrl: './analytics-widget.component.html',
  styleUrls: ['./analytics-widget.component.scss'],
})
export class AnalyticsWidgetComponent implements OnDestroy, OnInit {
  @Input() public widget: SnWidget;

  @Input() public viewId: string;

  public changeTitle = false;

  public mapStyle: GoogleMapStyle = GoogleMapStyle.Dark3D;
  public googleMapCenter: SnLonLatLike;

  public layers: any[] = [];
  public heatmaps: google.maps.visualization.HeatmapLayer[] = [];
  public deckGLoverlay;
  public deckGlLayers: Layer[] = [];
  public heatmap3DActive: boolean;
  public data3DLoaded: boolean = false;
  public is3DAvailable: boolean = false;

  private _popout: GoogleMapPopup;
  private _map: GoogleMap;
  private readonly _destroy$ = new Subject<void>();

  get map(): GoogleMap {
    return this._map;
  }

  constructor(
    private readonly _currentOrganization: SnCurrentOrganization,
    private readonly _cd: ChangeDetectorRef,
    private readonly _analyticsService: AnalyticsService,
    private readonly _matDialog: MatDialog,
    private readonly _timeService: FsTimeService,
  ) {
    this.googleMapCenter = this._currentOrganization.settings.geometry.coordinates;
  }

  public ngOnInit(): void {
    this.googleMapCenter = this.widget.meta.center ? this.widget.meta.center : this.googleMapCenter;
    this._analyticsService.loadedData$.pipe(takeUntil(this._destroy$)).subscribe((res) => {
      if (res) {
        res.forEach((layer) => {
          if (
            !this.layers.includes(layer) &&
            layer.layer.widgetId === this.widget.id &&
            this._map?.mapInstance
          ) {
            this.layers.push(layer);
            const data = layer.layer;
            if (data.layers.active) {
              switch (data.layers.mapDataType || data.layers.map_data_type) {
                case 'cad':
                  this._cadLayers(data.layers, data.loadedData);
                  break;
                case 'avl':
                  this._avlLayers(data.layers, data.loadedData);
                  break;
                case 'lpr':
                  this._lprLayers(data.layers, data.loadedData);
                  break;
                case 'users':
                  this._usersLayers(data.layers, data.loadedData);
                  break;
              }
            }
          }
        });
      }
    });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onMapInit(googleMap: GoogleMap): void {
    this._map = googleMap;
    this._map.setNewMapID(GoogleMapStyle.Dark3D);
    this._createOverlay();
    this._fetchData(false);
  }

  public toggleLayer(layer: any): void {
    const currentLayer = layer.layer.layers;
    if (currentLayer.mapOverlayStyle === 'heatmap') {
      if (currentLayer.layer_active) {
        currentLayer.layer_heatmap.setMap(null);
        currentLayer.layer_active = false;
      } else {
        currentLayer.layer_heatmap.setMap(this.map.mapInstance);
        currentLayer.layer_active = true;
      }
    }
    if (currentLayer.mapOverlayStyle === 'extrude') {
      let visible;
      if (currentLayer.layer_active) {
        visible = false;
        currentLayer.layer_active = false;
      } else {
        visible = true;
        currentLayer.layer_active = true;
      }
      this._updateLayer(`hexagon-layer-${currentLayer.layer_name}`, {
        visible,
      });
    }
    if (currentLayer.mapOverlayStyle === 'breadcrumbs') {
      if (currentLayer.layer_active) {
        currentLayer.layer_breadcrumb.forEach((breadcrumb) => {
          breadcrumb.polyline.setMap(null);
        });
        currentLayer.layer_active = false;
      } else {
        currentLayer.layer_breadcrumb.forEach((breadcrumb) => {
          breadcrumb.polyline.setMap(this.map.mapInstance);
        });
        currentLayer.layer_active = true;
      }
    }
  }

  public zoomIn(): void {
    this._map.setZoom(this._map.getZoom() + 1);
  }

  public zoomOut(): void {
    this._map.setZoom(this._map.getZoom() - 1);
  }

  public convertToRGB(c1: number, c2: number, c3: number): string {
    return `rgba(${c1}, ${c2}, ${c3}, 100%)`;
  }

  public editTitle(): void {
    this.changeTitle = true;
  }

  public setTitle(value: boolean): void {
    this.changeTitle = value;
    this._cd.detectChanges();
  }

  public setUpdateTitle(value: string): void {
    this.widget.name = value;
    this._cd.detectChanges();
  }

  public updateDateRange(layer: any): void {
    const dialogRef = this._matDialog.open(AnalyticsWidgetDateComponent, {
      data: layer,
      width: '600px',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((res) => {
        if (res) {
          layer.layers.createdTimestamp_Gte = this._timeService.convertToDateSeconds(
            res.event_timestamp__gte,
          );
          layer.layers.createdTimestamp_Lte = this._timeService.convertToDateSeconds(
            res.event_timestamp__lte,
          );

          const index = this.layers.findIndex((l) => {
            return l.layer.id === layer.id;
          });
          this.destroyLayers();
          this.layers[index] = layer.layers;
          this._updateWidgetMeta();
        }
      });
  }

  public destroyLayers(): void {
    this.layers.forEach((layer) => {
      const formattedLayer = layer.layer.layers;
      switch (formattedLayer.mapOverlayStyle || formattedLayer.map_overlay_style) {
        case 'breadcrumbs':
          formattedLayer.layer_breadcrumb.forEach((lay) => {
            lay.polyline.setMap(null);
          });
          formattedLayer.layer_breadcrumb = null;
          break;
        case 'heatmap':
          formattedLayer.layer_heatmap.setMap(null);
          formattedLayer.layer_heatmap = null;
          break;
      }
    });
  }

  public rebuildLayers(): void {
    this._analyticsService.loadedData.forEach((data) => {
      if (
        data.layer.widgetId === this.widget.id &&
        data.layer.layers.mapOverlayStyle !== 'extrude'
      ) {
        const formattedLayer = data.layer.layers;
        switch (formattedLayer.mapDataType || formattedLayer.map_data_type) {
          case 'avl':
            this._avlLayers(formattedLayer, data.layer.loadedData);
            break;
          case 'cad':
            this._cadLayers(formattedLayer, data.layer.loadedData);
            break;
          case 'lpr':
            this._lprLayers(formattedLayer, data.layer.loadedData);
            break;
          case 'users':
            this._usersLayers(formattedLayer, data.layer.loadedData);
            break;
        }
      }
    });
  }

  private _createHeatmapLayer(data: any, layer: any): void {
    const heatmapCoordinates = [];
    data.forEach((point) => {
      heatmapCoordinates.push({
        location: new google.maps.LatLng(point.lat, point.lon),
      });
    });
    const gradient = this._heatmapGradientConversion(layer.colors);
    layer.layer_heatmap = new google.maps.visualization.HeatmapLayer({
      data: heatmapCoordinates,
      maxIntensity: 10,
      radius: 10,
      opacity: 0.8,
      gradient,
    });
    layer.layer_heatmap.setMap(this._map.mapInstance);
    layer.layer_active = true;
  }

  private _updateLayer(id: string, newProps: unknown): void {
    const index = this.deckGlLayers.findIndex((layer) => layer.id === id);
    if (this.deckGlLayers[index]) {
      this.deckGlLayers[index] = this.deckGlLayers[index].clone(newProps);
      this.deckGLoverlay.setProps({ layers: Object.values(this.deckGlLayers) });
    }
    if (index === -1) {
      if (this.deckGlLayers.length) {
        this._renderLayers(this.deckGlLayers);
      } else {
        this.deckGlLayers = [];
        this._renderLayers(this.deckGlLayers);
      }
    }
  }

  private _createBreadcrumbs(data: any, layer: any): void {
    const polylines = [];

    data.forEach((points) => {
      const color = this._getRandomLightColor();
      const linePoints = points.formmattedResults.map((d) => new google.maps.LatLng(d.lat, d.lon));
      const lineSymbol = {
        path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
        fillOpacity: 1,
        strokeOpacity: 1,
        strokeWeight: 2,
        fillColor: color,
        strokeColor: tinycolor(color).darken(75),
        scale: 4,
      };

      const polyline = new google.maps.Polyline({
        path: linePoints.reverse(),
        strokeOpacity: 1,
        strokeColor: this._breadcrumbColorConversion(layer.colors[1]),
        strokeWeight: 2,
        draggable: false,
        icons: [
          {
            icon: lineSymbol,
            offset: '0',
            repeat: '50px',
          },
        ],
      });

      polyline.addListener('mouseover', (e) => {
        polyline.setOptions({
          strokeWeight: 10,
        });
        this._popout = new GoogleMapPopup(PolylineInfoPopoutComponent, {
          data: points,
          lonLat: [e.latLng.lng(), e.latLng.lat()],
        });
        this._popout.open(this.map);
      });

      polyline.addListener('mouseout', (e) => {
        polyline.setOptions({
          strokeWeight: 2,
        });
        this._popout.close();
      });
      polyline.setMap(this._map.mapInstance);
      polylines.push({ polyline });
    });
    layer.layer_active = true;
    layer.layer_breadcrumb = polylines;
  }

  private _createExtrudeLayer(data: any, layer: any): void {
    const name = this._convertLayerName(layer.name);
    const hexagonLayer = new HexagonLayer({
      id: `hexagon-layer-${name}`,
      data,
      coverage: 1,
      elevationScale: 25,
      elevationRange: [0, 750],
      extruded: true,
      visible: true,
      getPosition: (d) => [d.lon, d.lat],
      pickable: true,
      radius: 100,
      upperPercentile: 100,
      colorRange: layer.colors,
      colorScaleType: 'quantize',
    });

    layer.layer_active = true;
    this.deckGlLayers = [...this.deckGlLayers, hexagonLayer];
    this._renderLayers(this.deckGlLayers);
  }

  private _createOverlay(): void {
    this.deckGLoverlay = new DeckOverlay();
  }

  private _renderLayers(layers: Layer[]): void {
    this.deckGLoverlay.setProps({
      layers,
      parameters: {
        depthTest: true,
        blend: false,
      },
      glOptions: {
        preserveDrawingBuffer: true,
      },
      getTooltip: this._getTooltip,
    });

    this.deckGLoverlay.setMap(this._map.mapInstance);
  }

  private _getTooltip(info: any): any {
    return (
      info.object &&
      `\
    ${info.object.points.length} alerts`
    );
  }

  private _avlLayers(layer: any, data: any): void {
    if (layer.mapOverlayStyle === 'extrude' || layer.map_overlay_style === 'extrude') {
      this.heatmap3DActive = true;
      this.data3DLoaded = true;
      this._createExtrudeLayer(data, layer);
    }
    if (layer.mapOverlayStyle === 'heatmap' || layer.map_overlay_style === 'heatmap') {
      this._createHeatmapLayer(data, layer);
    }
    if (layer.mapOverlayStyle === 'breadcrumbs' || layer.map_overlay_style === 'breadcrumbs') {
      this._createBreadcrumbs(data, layer);
    }

    this._cd.detectChanges();
  }

  private _cadLayers(layer: any, data: any): void {
    if (layer.mapOverlayStyle === 'extrude' || layer.map_overlay_style === 'extrude') {
      this.heatmap3DActive = true;
      this.data3DLoaded = true;
      this._createExtrudeLayer(data, layer);
    }
    if (layer.mapOverlayStyle === 'heatmap' || layer.map_overlay_style === 'heatmap') {
      this._createHeatmapLayer(data, layer);
    }
  }

  private _lprLayers(layer: any, data: any): void {
    if (layer.mapOverlayStyle === 'extrude' || layer.map_overlay_style === 'extrude') {
      this.heatmap3DActive = true;
      this.data3DLoaded = true;
      this._createExtrudeLayer(data, layer);
    }
    if (layer.mapOverlayStyle === 'heatmap' || layer.map_overlay_style === 'heatmap') {
      this._createHeatmapLayer(data, layer);
    }
    this._cd.detectChanges();
  }

  private _usersLayers(layer: any, data: any): void {
    if (layer.mapOverlayStyle === 'extrude' || layer.map_overlay_style === 'extrude') {
      this.heatmap3DActive = true;
      this.data3DLoaded = true;
      this._createExtrudeLayer(data, layer);
    }
    if (layer.mapOverlayStyle === 'heatmap' || layer.map_overlay_style === 'heatmap') {
      this._createHeatmapLayer(data, layer);
    }
    if (layer.mapOverlayStyle === 'breadcrumbs' || layer.map_overlay_style === 'breadcrumbs') {
      this._createBreadcrumbs(data, layer);
    }
    this._cd.detectChanges();
  }

  private _heatmapGradientConversion(colors: number[]): string[] {
    const convertedGradientColors: string[] = [];
    convertedGradientColors.push('rgba(0, 255, 255, 0)');
    colors.forEach((color) => {
      convertedGradientColors.push(`rgba(${color[0]}, ${color[1]}, ${color[2]}, 1)`);
    });
    return convertedGradientColors;
  }

  private _breadcrumbColorConversion(color: number[]): string {
    return tinycolor({ r: color[0], g: color[1], b: color[2] }).toHexString();
  }

  private _convertLayerName(name: string): string {
    return name.replace(/\s/g, '');
  }

  private _fetchData(refreshLayers: boolean): void {
    this._analyticsService.checkIfLoadedData(this.widget, refreshLayers);
  }

  private _getRandomLightColor(): string {
    return `hsl(${Math.floor(Math.random() * 30) * 12}, 100%, 75%)`;
  }

  private _updateWidgetMeta(): void {
    this._analyticsService
      .updateWidgetMeta(this.viewId, this.widget.id, this.layers)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.layers = [];
        this._fetchData(true);
      });
  }
}
